import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { collection, query, where, getDocs, addDoc, serverTimestamp, orderBy, doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  ChatBubbleLeftIcon
} from '@heroicons/react/24/outline';
import {
  ArrowUpIcon as ArrowUpSolidIcon,
  ArrowDownIcon as ArrowDownSolidIcon
} from '@heroicons/react/24/solid';

interface CommunityResponse {
  id: string;
  debateId: string;
  roundIndex: number;
  speaker: string;
  content: string;
  authorId: string;
  authorName: string;
  timestamp: string;
  upvotes: number;
  downvotes: number;
  votes: { [userId: string]: 'upvote' | 'downvote' };
  replyCount: number;
  parentId?: string;
}

interface AIPointDiscussionProps {
  debateId: string;
  roundIndex: number;
  speaker: string;
}

export const AIPointDiscussion: React.FC<AIPointDiscussionProps> = ({ debateId, roundIndex, speaker }) => {
  const { user } = useAuth();
  const [responses, setResponses] = useState<CommunityResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [newResponseContent, setNewResponseContent] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchResponses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debateId, roundIndex]);

  const fetchResponses = async () => {
    try {
      setLoading(true);
      const responsesRef = collection(db, 'community_responses');
      const q = query(
        responsesRef,
        where('debateId', '==', debateId),
        where('roundIndex', '==', roundIndex),
        orderBy('timestamp', 'desc')
      );
      const snapshot = await getDocs(q);

      const fetchedResponses: CommunityResponse[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        fetchedResponses.push({
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate?.()?.toISOString() || data.timestamp,
        } as CommunityResponse);
      });

      setResponses(fetchedResponses);
    } catch (err) {
      console.error('Error fetching responses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitResponse = async () => {
    if (!user || !newResponseContent.trim()) return;

    try {
      setIsSubmitting(true);
      await addDoc(collection(db, 'community_responses'), {
        debateId,
        roundIndex,
        speaker,
        content: newResponseContent.trim(),
        authorId: user.uid,
        authorName: user.email?.split('@')[0] || 'Anonymous',
        timestamp: serverTimestamp(),
        upvotes: 0,
        downvotes: 0,
        votes: {},
        replyCount: 0,
      });

      setNewResponseContent('');
      await fetchResponses();
    } catch (err) {
      console.error('Error submitting response:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitReply = async (parentId: string) => {
    if (!user || !replyContent.trim()) return;

    try {
      setIsSubmitting(true);
      await addDoc(collection(db, 'community_responses'), {
        debateId,
        roundIndex,
        speaker,
        parentId,
        content: replyContent.trim(),
        authorId: user.uid,
        authorName: user.email?.split('@')[0] || 'Anonymous',
        timestamp: serverTimestamp(),
        upvotes: 0,
        downvotes: 0,
        votes: {},
        replyCount: 0,
      });

      // Increment parent reply count
      const parentRef = doc(db, 'community_responses', parentId);
      await updateDoc(parentRef, {
        replyCount: increment(1),
      });

      setReplyContent('');
      setReplyingTo(null);
      await fetchResponses();
    } catch (err) {
      console.error('Error submitting reply:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVote = async (responseId: string, voteType: 'upvote' | 'downvote') => {
    if (!user) return;

    try {
      const response = responses.find(r => r.id === responseId);
      if (!response) return;

      const currentVote = response.votes[user.uid];
      const newVote = currentVote === voteType ? null : voteType;

      let upvoteChange = 0;
      let downvoteChange = 0;

      if (currentVote === 'upvote') upvoteChange = -1;
      if (currentVote === 'downvote') downvoteChange = -1;
      if (newVote === 'upvote') upvoteChange += 1;
      if (newVote === 'downvote') downvoteChange += 1;

      const responseRef = doc(db, 'community_responses', responseId);
      const updates: any = {};
      if (upvoteChange !== 0) updates.upvotes = increment(upvoteChange);
      if (downvoteChange !== 0) updates.downvotes = increment(downvoteChange);
      if (newVote) {
        updates[`votes.${user.uid}`] = newVote;
      } else {
        updates[`votes.${user.uid}`] = null;
      }

      await updateDoc(responseRef, updates);
      await fetchResponses();
    } catch (err) {
      console.error('Error voting:', err);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const topLevelResponses = responses.filter(r => !r.parentId);
  const getReplies = (parentId: string) => responses.filter(r => r.parentId === parentId);

  return (
    <div className="bg-gray-800/50 border-t border-gray-700 p-4">
      <h4 className="text-sm font-semibold text-gray-300 mb-3">
        Discussion ({topLevelResponses.length})
      </h4>

      {/* New Response Form */}
      {user ? (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 mb-4">
          <textarea
            value={newResponseContent}
            onChange={(e) => setNewResponseContent(e.target.value)}
            placeholder="Add your thoughts on this point..."
            rows={3}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical mb-2"
            disabled={isSubmitting}
          />
          <div className="flex justify-end">
            <button
              onClick={handleSubmitResponse}
              className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting || !newResponseContent.trim()}
            >
              {isSubmitting ? 'Posting...' : 'Post'}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 mb-4 text-center text-gray-400 text-sm">
          Log in to join the discussion
        </div>
      )}

      {/* Responses List */}
      {loading ? (
        <div className="text-center py-4 text-gray-400 text-sm">Loading...</div>
      ) : topLevelResponses.length === 0 ? (
        <div className="text-center py-6">
          <ChatBubbleLeftIcon className="w-12 h-12 text-gray-600 mx-auto mb-2" />
          <p className="text-gray-400 text-sm">No responses yet. Be the first!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {topLevelResponses.map((response) => {
            const userVote = user && response.votes[user.uid] ? response.votes[user.uid] : null;
            const replies = getReplies(response.id);

            return (
              <div key={response.id} className="bg-gray-800 border border-gray-700 rounded-lg p-3">
                {/* Author and Timestamp */}
                <div className="flex items-center gap-2 text-xs text-gray-400 mb-2">
                  <span className="font-medium text-gray-300">{response.authorName}</span>
                  <span>•</span>
                  <span>{formatTimestamp(response.timestamp)}</span>
                </div>

                {/* Content */}
                <div className="text-gray-200 text-sm whitespace-pre-wrap mb-3">
                  {response.content}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 text-xs">
                  <button
                    onClick={() => handleVote(response.id, 'upvote')}
                    className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                      userVote === 'upvote'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                    }`}
                    disabled={!user}
                  >
                    {userVote === 'upvote' ? <ArrowUpSolidIcon className="w-3 h-3" /> : <ArrowUpIcon className="w-3 h-3" />}
                    {response.upvotes}
                  </button>
                  <button
                    onClick={() => handleVote(response.id, 'downvote')}
                    className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                      userVote === 'downvote'
                        ? 'bg-red-600 text-white'
                        : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                    }`}
                    disabled={!user}
                  >
                    {userVote === 'downvote' ? <ArrowDownSolidIcon className="w-3 h-3" /> : <ArrowDownIcon className="w-3 h-3" />}
                    {response.downvotes}
                  </button>
                  <button
                    onClick={() => setReplyingTo(replyingTo === response.id ? null : response.id)}
                    className="flex items-center gap-1 px-2 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
                    disabled={!user}
                  >
                    <ChatBubbleLeftIcon className="w-3 h-3" />
                    Reply ({response.replyCount})
                  </button>
                </div>

                {/* Reply Form */}
                {replyingTo === response.id && (
                  <div className="mt-3 pl-4 border-l-2 border-gray-600">
                    <textarea
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      placeholder="Write a reply..."
                      rows={2}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical mb-2"
                      disabled={isSubmitting}
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => {
                          setReplyingTo(null);
                          setReplyContent('');
                        }}
                        className="px-3 py-1 text-sm bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                        disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleSubmitReply(response.id)}
                        className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                        disabled={isSubmitting || !replyContent.trim()}
                      >
                        {isSubmitting ? 'Posting...' : 'Reply'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Replies */}
                {replies.length > 0 && (
                  <div className="mt-3 pl-4 border-l-2 border-gray-600 space-y-2">
                    {replies.map((reply) => {
                      const replyUserVote = user && reply.votes[user.uid] ? reply.votes[user.uid] : null;

                      return (
                        <div key={reply.id} className="bg-gray-750 rounded-lg p-2">
                          <div className="flex items-center gap-2 text-xs text-gray-400 mb-2">
                            <span className="font-medium text-gray-300">{reply.authorName}</span>
                            <span>•</span>
                            <span>{formatTimestamp(reply.timestamp)}</span>
                          </div>
                          <div className="text-gray-200 text-sm whitespace-pre-wrap mb-2">
                            {reply.content}
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <button
                              onClick={() => handleVote(reply.id, 'upvote')}
                              className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                                replyUserVote === 'upvote'
                                  ? 'bg-green-600 text-white'
                                  : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                              }`}
                              disabled={!user}
                            >
                              {replyUserVote === 'upvote' ? <ArrowUpSolidIcon className="w-3 h-3" /> : <ArrowUpIcon className="w-3 h-3" />}
                              {reply.upvotes}
                            </button>
                            <button
                              onClick={() => handleVote(reply.id, 'downvote')}
                              className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                                replyUserVote === 'downvote'
                                  ? 'bg-red-600 text-white'
                                  : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                              }`}
                              disabled={!user}
                            >
                              {replyUserVote === 'downvote' ? <ArrowDownSolidIcon className="w-3 h-3" /> : <ArrowDownIcon className="w-3 h-3" />}
                              {reply.downvotes}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
